import React, { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Grid,
  Layout,
  Menu,
  Space,
  Typography,
} from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import {
  BrowserRouter,
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import './App.css';
import LoginForm from './components/LoginForm';
import Console from './components/Console';
import Authorize from './components/Authorize';
import MyApps from './components/MyApps';
import AdminMyApps from './components/AdminMyApps';
import Security from './components/Security';
import Reset2FA from './components/Reset2FA';
import AdminDelegates from './components/AdminDelegates';
import DashboardSkeleton from './components/DashboardSkeleton';
import About from './components/About';
import Docs from './components/Docs';
import MyQR from './components/MyQR';
import UsersAPI from './api/user';
import AuthAPI from './api/auth';
import Cookies from 'universal-cookie';
import TicketsAPI from './api/ticket';
import { ThemeProvider } from './theme/ThemeProvider';
import ThemePicker from './theme/ThemePicker';
import ThemeSync from './theme/ThemeSync';


const { Title } = Typography;

const SYS_ADMIN_BIT = 8;
const TOTP_RESETTER_BIT = 16;

function isAdmin(user) {
  return !!(user && (Number(user.sys_roles) & SYS_ADMIN_BIT));
}

// Admins and helpdesk delegates may both reach the 2FA reset page.
function canReset2FA(user) {
  return !!(user && (Number(user.sys_roles) & (SYS_ADMIN_BIT | TOTP_RESETTER_BIT)));
}

// The four everyday pages, always visible in the header.
export const PRIMARY_ITEMS = [
  { key: '/', label: 'Home' },
  { key: '/security', label: 'Security' },
  { key: '/myqr', label: 'My QR' },
  { key: '/docs', label: 'Docs' },
];

// Role-specific pages, grouped by audience. The group header carries the
// audience, so the items themselves stay short — no "Admin — " prefixes.
export function secondaryGroups(user) {
  const groups = [{
    type: 'group',
    label: 'Developer',
    children: [{ key: '/myapps', label: 'My Apps' }],
  }];
  if (canReset2FA(user)) {
    groups.push({
      type: 'group',
      label: 'Helpdesk',
      children: [{ key: '/reset-2fa', label: 'Reset 2FA' }],
    });
  }
  if (isAdmin(user)) {
    groups.push({
      type: 'group',
      label: 'Administration',
      children: [
        { key: '/admin/myapps', label: 'App Requests' },
        { key: '/admin/delegates', label: 'Delegates' },
      ],
    });
  }
  return groups;
}

function App() {

  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!user) {
      return;
    }
    TicketsAPI.search({},
      (res) => {
        setSessions(res);
      },
      (err) => {
        console.log(err);
      }
    )
    return () => {

    }
  }, [user]);


  const cookies = new Cookies();

  let service = null;
  try {
    const st_requestService = window.atob(cookies.get('SSO_requestService'));
    console.log('requestService', st_requestService);
    service = JSON.parse(st_requestService);
  } catch (e) {
    console.log(e);
  }

  const logout = () => {
    AuthAPI.logout(
      (res) => {
        setUser(null);
      },
      (err) => {
        console.log(err);
      });
  }

  const onLogin = () => {
    setIsLoading(true);
    UsersAPI.me(
      (user) => {
        setUser(user);
        setIsLoading(false);
      },
      (err) => {
        setUser(null);
        setIsLoading(false);
      })
  }

  useEffect(() => {
    onLogin();
    return () => {

    }
  }, []);


  const homeContent = <>
    {!user && <LoginForm onLogin={onLogin} />}
    {user && <>
      {service && <Authorize user={user} service={service} />}
      <Console sessions={sessions} user={user} onLogout={logout} />
    </>}
  </>;

  const gate = (node) => {
    if (!user) return <LoginForm onLogin={onLogin} />;
    return node;
  };

  return (
    <ThemeProvider>
      <ThemeSync user={user} />
      <BrowserRouter basename="/html">
        <Layout style={{ minHeight: '100vh' }}>
          <AppHeader user={user} service={service} />
          <Layout.Content style={{ padding: '24px 16px' }}>
            <div style={{ maxWidth: 960, margin: '0 auto' }}>
              {isLoading ? (
                <DashboardSkeleton />
              ) : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Routes>
                    <Route path="/" element={homeContent} />
                    <Route path="/about" element={<About />} />
                    <Route path="/docs" element={<Docs />} />
                    <Route path="/myapps" element={gate(<MyApps user={user} />)} />
                    <Route path="/security" element={gate(<Security />)} />
                    <Route path="/myqr" element={gate(<MyQR />)} />
                    <Route
                      path="/admin/myapps"
                      element={
                        user
                          ? (isAdmin(user) ? <AdminMyApps user={user} /> : <Navigate to="/" replace />)
                          : <LoginForm onLogin={onLogin} />
                      }
                    />
                    <Route
                      path="/admin/delegates"
                      element={
                        user
                          ? (isAdmin(user) ? <AdminDelegates /> : <Navigate to="/" replace />)
                          : <LoginForm onLogin={onLogin} />
                      }
                    />
                    <Route
                      path="/reset-2fa"
                      element={
                        user
                          ? (canReset2FA(user) ? <Reset2FA user={user} /> : <Navigate to="/" replace />)
                          : <LoginForm onLogin={onLogin} />
                      }
                    />
                    {/* kept so existing bookmarks keep working */}
                    <Route path="/admin/reset-2fa" element={<Navigate to="/reset-2fa" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Space>
              )}
            </div>
          </Layout.Content>
          <Layout.Footer
            style={{
              textAlign: 'center',
              borderTop: '1px solid var(--ant-color-border-secondary, rgba(0, 0, 0, 0.06))',
              padding: '20px 16px',
            }}
          >
            <Space split={<Typography.Text type="secondary">·</Typography.Text>} wrap>
              <RouterLink to="/docs">Docs</RouterLink>
              <RouterLink to="/about">About</RouterLink>
              <a href="https://www.it.chula.ac.th/" target="_blank" rel="noreferrer">
                Office of IT
              </a>
            </Space>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginTop: 8, fontSize: 12 }}
            >
              ChulaSSO 2.0 · A solution by Krerk Piromsopa, Ph.D. for Chulalongkorn University
            </Typography.Text>
          </Layout.Footer>
        </Layout>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function AppHeader({ user, service }) {
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showNav = !!user && !service;
  const isCompact = !screens.md;

  const groups = secondaryGroups(user);
  // Desktop: everything past the four everyday pages collapses under "More".
  const menuItems = [...PRIMARY_ITEMS, { key: 'more', label: 'More', children: groups }];
  // Drawer: vertical room is cheap, so show the groups flat — a collapsed
  // "More" would only add a tap.
  const drawerItems = [...PRIMARY_ITEMS, { type: 'divider' }, ...groups];

  // Antd highlights the selected child inside the popup but leaves the
  // collapsed parent unstyled, so the bar looks unselected on overflow routes.
  const inOverflow = groups.some((g) =>
    g.children.some((c) => c.key === location.pathname));
  const selectedKeys = [location.pathname];
  if (inOverflow) selectedKeys.push('more');

  const onNavClick = ({ key }) => {
    navigate(key);
    setDrawerOpen(false);
  };

  return (
    <Layout.Header style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      padding: isCompact ? '0 16px' : undefined,
      boxShadow: '0 1px 0 rgba(0, 0, 0, 0.06)',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      <Space size={12} align="center" style={{ minWidth: 0 }}>
        <img src="/html/logo-chula-color.svg" height={36} alt="Chula" style={{ display: 'block' }} />
        {screens.xs ? null : (
          <Title level={4} style={{ margin: 0, whiteSpace: 'nowrap' }}>SSO 2.0</Title>
        )}
      </Space>
      <Space size={4} align="center" style={{ flex: 1, justifyContent: 'flex-end' }}>
        {showNav && !isCompact && (
          <Menu
            mode="horizontal"
            selectedKeys={selectedKeys}
            onClick={onNavClick}
            items={menuItems}
            style={{ background: 'transparent', borderBottom: 'none', minWidth: 0, flex: 'none' }}
          />
        )}
        <ThemePicker />
        {showNav && isCompact && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          />
        )}
      </Space>
      <Drawer
        title="Menu"
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={Math.min(280, typeof window !== 'undefined' ? window.innerWidth - 48 : 280)}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          onClick={onNavClick}
          items={drawerItems}
          style={{ borderInlineEnd: 'none' }}
        />
      </Drawer>
    </Layout.Header>
  );
}

export default App;
